import * as React from 'react';
// mui
import {
  Box,
  IconButton,
  ListItemButton,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  Divider,
  ListItemText,
  TextField,
  Tooltip,
} from '@mui/material';
// icons
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
// dayjs
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
// redux
import { useSelector } from 'react-redux';
import { selectTaskById } from '../../../store/selectors/taskSelectors';
// types
import Task from '../../../interfaces/Task.interface';
import MiniTask from '../../../interfaces/MiniTask.interface';
// axios
import api from '../../../axios';

interface MiniTaskListProps {
  padding: number;
  parentTaskId: number;
  isCompletedList: boolean;
}

export default function MiniTaskList(props: MiniTaskListProps) {
  // hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // props
  const parentTaskId: number | null = props.parentTaskId;
  const parentTask: Task = useSelector(selectTaskById(parentTaskId)) as Task;
  const isCompletedList = props.isCompletedList;
  const miniTaskList: MiniTask[] = parentTask.miniTasks
    .filter((miniTask: MiniTask) => miniTask.completed === isCompletedList)
    .sort((a, b) => a.title.localeCompare(b.title)); // sort alphabetically
  const inheritedPadding: number = isMobile ? props.padding / 2 : props.padding;

  return (
    <Box>
      { miniTaskList.map((item: MiniTask) => (
        <MiniTaskListItem
          key={item.id}
          item={item}
          inheritedPadding={inheritedPadding}
          isMobile={isMobile}
          parentTask={parentTask}
        />
      )) }
    </Box>
  );
}

interface MiniTaskListItemProps {
  item: MiniTask;
  inheritedPadding: number;
  isMobile: boolean;
  parentTask: Task;
}

function MiniTaskListItem({ item, inheritedPadding, isMobile, parentTask }: MiniTaskListItemProps) {
  const [itemIsHovered, setItemIsHovered] = React.useState(false);
  const [isInEditMode, setIsInEditMode] = React.useState(false);
  const [localTitleText, setLocalTitleText] = React.useState(item.title);

  const onCancelTitleText = () => {
    setLocalTitleText(item.title);
    setIsInEditMode(false);
  };

  const onSaveTitleText = (newTitle: string) => {
    api.put(`/tasks/mini/update/${item.id}`, { title: newTitle })
      .catch(console.error)
      .finally(() => setIsInEditMode(false));
  };

  const onCompleteOrUncomplete = () => {
    api.put(`/tasks/mini/update/${item.id}`, { completed: !item.completed })
      .catch(console.error);
  };

  const onDeletePress = () => {
    api.delete(`/tasks/mini/delete/${item.id}`).catch(console.error);
  };

  return (
    <>
      <ListItemButton
        onClick={(e) => { e.stopPropagation(); if (!isInEditMode) setIsInEditMode(true); }}
        sx={{
          pl: inheritedPadding + 1,
          pr: isMobile ? 1 : '',
          '&.Mui-focusVisible': {
            backgroundColor: 'transparent', // removes focus highlight
          },
          '&.Mui-selected': {
            backgroundColor: 'transparent', // disable seelct highlight
          },
        }}
        onMouseEnter={() => setItemIsHovered(true)}
        onMouseLeave={() => setItemIsHovered(false)}
      >
        <Tooltip title={item.completed ? 'Uncomplete' : 'Complete'}>
          <ListItemIcon
            sx={{ minWidth: isMobile ? 36 : 46 }}
          >
            <IconButton
              sx={{ p: isMobile ? 1 : '', color: item.completed ? 'grey' : 'inherit' }}
              disabled={parentTask.completed}
              onClick={(e) => { e.stopPropagation(); onCompleteOrUncomplete(); }}
            >
              {item.completed ? <CheckCircleOutlineIcon /> : <PanoramaFishEyeIcon />}
            </IconButton>
          </ListItemIcon>
        </Tooltip>

        {isInEditMode ? (
          <TextField
            size="small"
            variant="standard"
            autoFocus
            fullWidth
            onBlur={onCancelTitleText}
            onChange={(e) => setLocalTitleText(e.target.value)}
            onKeyDown={(e) => { if (e.code === 'Enter') onSaveTitleText(localTitleText); }}
            value={localTitleText}
          />
        ) : (
          <ListItemText
            sx={{ color: item.completed ? 'grey' : 'inherit' }}
            primary={item.title}
          />
        )}

        {(isMobile || itemIsHovered) && !isInEditMode && (
          <IconButton
            sx={{ color: 'lightgrey' }}
            onClick={(e) => { e.stopPropagation(); onDeletePress(); }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </ListItemButton>
      <Divider sx={{ ml: (isMobile ? inheritedPadding + 1 : inheritedPadding), mr: isMobile ? '8px' : 'auto' }} />
    </>
  );
}
